/**
 * Migration Note: seedDiscriminated.types.ts (formerly SeedDiscriminated.ts)
 *
 * Previous location: src/sections/seeds/types/SeedDiscriminated.ts
 * New location: src/sections/seeds/types/seedDiscriminated.types.ts
 * Migrated: 2025-10-22 (Phase 2)
 * Reason: Standardize type file naming with .types.ts suffix and camelCase
 *
 * ENG-5427 (2026-08-01):
 * - Removed PreseedSeed from the union. Preseeds are NOT discriminated by
 *   `class` — they carry no `class` field on the wire at all — so a `class`-
 *   discriminated union was the wrong home for them, and `isSeedPreseed`
 *   narrowed on a value that never arrives.
 * - Classify a preseed with `isPreseedKey` (`utils/preseedKey.ts`) instead.
 */

import { SeedDomain } from './seedDomain.types';
import { SeedIP } from './seedIp.types';
import { SeedWebApplication } from './seedWebApplication.types';

/**
 * Discriminated union of the three real seed classes
 *
 * Discriminated by the `class` field which determines the seed's type.
 * Use type guards for type-safe narrowing in conditional logic.
 *
 * Preseeds are deliberately NOT members. A preseed row arrives from the API
 * with no `class` field, so it cannot participate in a `class`-discriminated
 * union; it is identified by its `#preseed#` key prefix via `isPreseedKey`
 * (`@/sections/seeds/utils/preseedKey`). Do not re-add a `'preseed'` variant to
 * make a call site typecheck — that is the shape-guessing ENG-5427 retired.
 *
 * @example
 * ```typescript
 * const renderSeedDetails = (seed: Seed) => {
 *   if (isSeedDomain(seed)) {
 *     // TypeScript knows: seed is SeedDomain
 *     return <span>{seed.email}</span>;
 *   }
 *   if (isSeedIP(seed)) {
 *     // TypeScript knows: seed is SeedIP
 *     return <span>{seed.country}</span>;
 *   }
 *   // ... handle other types
 * };
 * ```
 */
export type Seed = SeedDomain | SeedIP | SeedWebApplication;

/**
 * Alias for Seed discriminated union (used in implementation plan)
 *
 * Provides a semantic name for the unified seed entity type.
 * Use this when referring to any seed type in generic contexts.
 */
export type SeedEntity = Seed;

/**
 * Type guard for Domain seeds
 *
 * @param seed - Seed to check
 * @returns true if seed is a SeedDomain
 */
export const isSeedDomain = (seed: Seed): seed is SeedDomain =>
  seed.class === 'domain' || seed.class === 'tld';

/**
 * Type guard for IP/CIDR seeds
 *
 * @param seed - Seed to check
 * @returns true if seed is a SeedIP
 */
export const isSeedIP = (seed: Seed): seed is SeedIP =>
  seed.class === 'ip' ||
  seed.class === 'cidr' ||
  seed.class === 'ipv4' ||
  seed.class === 'ipv6';

/**
 * Type guard for Web Application seeds
 *
 * @param seed - Seed to check
 * @returns true if seed is a SeedWebApplication
 */
export const isSeedWebApplication = (seed: Seed): seed is SeedWebApplication =>
  seed.class === 'webapplication';
